import { createSlice } from "@reduxjs/toolkit";
import dayjs from "dayjs"
import utcPlugin from "dayjs/plugin/utc"
import timezonePlugin from "dayjs/plugin/timezone"
import { DEFAULT_TIMEZONE } from "utils/generalSettingsHelpers"

dayjs.extend(utcPlugin)
dayjs.extend(timezonePlugin)

const SLICE_NAME = "currentSettings"

const INITIAL_STATE = {
  organizationClockFormat: "",
  organizationId: null,
  organizationColor: "",
  organizationShortName: "",
  organizationSubdomain: "",
  timezone: undefined,
  // The timezone above is whatever the organization has configured, and is
  // undefined when it has configured none, which is what lets the general
  // settings page show that no choice has been made.  Calculations that need a
  // real zone use this one instead.
  effectiveTimezone: undefined,
  taskReminders: undefined,
  taskNotifySettings: [],
}

export const currentSettingsSlice = createSlice({
  name: SLICE_NAME,
  initialState: INITIAL_STATE,
  reducers: {
    setInitialSettings: (state, action) => {
      Object.assign(state, action.payload)
      dayjs.tz.setDefault(action.payload.effectiveTimezone)
    },
    setClockFormat: (state, action) => {
      state.organizationClockFormat = action.payload
    },
    setColor: (state, action) => {
      state.organizationColor = action.payload
    },
    setShortName: (state, action) => {
      state.organizationShortName = action.payload
    },
    // The payload is the chosen timezone itself, and is blank when the choice
    // is cleared, so the effective timezone falls back the way
    // Organization#effective_time_zone does.  Everything reading the dayjs
    // default needs a real zone, which is why it is set from the effective one.
    setTimezone: (state, action) => {
      state.timezone = action.payload
      state.effectiveTimezone = action.payload || DEFAULT_TIMEZONE
      dayjs.tz.setDefault(state.effectiveTimezone)
    },
    setTaskReminders: (state, action) => {
      state.taskReminders = action.payload
    },
    enableTaskNotifySetting: (state, action) => {
      const type = action.payload
      const setting = state.taskNotifySettings.find(({ settingType }) => settingType === type)
      setting.isEnabled = true
    },
    disableTaskNotifySetting: (state, action) => {
      const type = action.payload
      const setting = state.taskNotifySettings.find(({ settingType }) => settingType === type)
      setting.isEnabled = false
    },
  },
})

export const {
  setInitialSettings,
  setClockFormat,
  setColor,
  setShortName,
  setTimezone,
  setTaskReminders,
  enableTaskNotifySetting,
  disableTaskNotifySetting,
} = currentSettingsSlice.actions

export const getOrganizationId = (state) => state[SLICE_NAME].organizationId

export const getOrganizationShortName = (state) => state[SLICE_NAME].organizationShortName

export const getOrganizationSubdomain = (state) => state[SLICE_NAME].organizationSubdomain

export const getColor = (state) => state[SLICE_NAME].organizationColor

export const getClockFormat = (state) => state[SLICE_NAME].organizationClockFormat

export const getTimezone = (state) => state[SLICE_NAME].timezone

export const getEffectiveTimezone = (state) => state[SLICE_NAME].effectiveTimezone

export const getTaskReminders = (state) => state[SLICE_NAME].taskReminders

export const getTaskNotifySettings = (state) => state[SLICE_NAME].taskNotifySettings

export const isTaskNotifySettingEnabled = (type) => (state) => (
  state[SLICE_NAME].taskNotifySettings.find((setting) => setting.settingType === type).isEnabled
)

export default currentSettingsSlice.reducer
